import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { Launch, Assessment, Timeline } from '@mui/icons-material';
import LabReports from '../reports/clinical/LabReports';

// Mock patient data for demo
const mockPatients = [
  {
    id: 'patient-1',
    name: 'John Doe',
    mrn: 'MRN-001',
    dateOfBirth: '1985-03-15',
    gender: 'Male'
  },
  {
    id: 'patient-2', 
    name: 'Jane Smith',
    mrn: 'MRN-002',
    dateOfBirth: '1978-08-22',
    gender: 'Female'
  },
  {
    id: 'patient-3',
    name: 'Robert Johnson', 
    mrn: 'MRN-003',
    dateOfBirth: '1965-11-08',
    gender: 'Male'
  }
];

const LabReportsDemo = () => {
  const [selectedPatient, setSelectedPatient] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [embeddedMode, setEmbeddedMode] = useState(true);

  const handlePatientSelect = (patientId) => {
    setSelectedPatient(patientId);
  };

  const openInDialog = () => {
    if (!selectedPatient) {
      alert('Please select a patient first');
      return;
    }
    setDialogOpen(true);
  };

  const openInModal = () => {
    if (!selectedPatient) {
      alert('Please select a patient first');
      return;
    }
    setModalOpen(true);
  };

  const selectedPatientData = mockPatients.find(p => p.id === selectedPatient);

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 4, background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)', color: 'white' }}>
        <Typography variant="h4" gutterBottom>
          Lab Reports Component Demo
        </Typography>
        <Typography variant="body1">
          Comprehensive lab reporting with trends analysis, 2D tabular view, and date range filtering
        </Typography>
      </Paper>

      {/* Features Overview */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Assessment sx={{ fontSize: 40, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Trends & Analysis
              </Typography>
              <Typography variant="body2" color="textSecondary">
                2D table view with test values over time. Rows show test analytes, columns show collection dates.
                Visual indicators for abnormal values.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Timeline sx={{ fontSize: 40, color: 'info.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Reports by Date
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Chronological view of lab reports with expandable details. Shows collection dates, 
                providers, and individual test results.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Launch sx={{ fontSize: 40, color: 'success.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Reusable Component
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Can be embedded in any parent container - encounters, patient charts, reports dashboard,
                or used as a standalone page.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Controls */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Demo Controls
        </Typography>
        
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Select Patient</InputLabel>
              <Select
                value={selectedPatient}
                onChange={(e) => handlePatientSelect(e.target.value)}
                label="Select Patient"
              >
                {mockPatients.map(patient => (
                  <MenuItem key={patient.id} value={patient.id}>
                    {patient.name} (MRN: {patient.mrn})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button 
                variant="contained" 
                onClick={openInDialog}
                disabled={!selectedPatient}
              >
                Open in Dialog
              </Button>
              <Button 
                variant="outlined" 
                onClick={() => setEmbeddedMode(!embeddedMode)}
              >
                {embeddedMode ? 'Hide' : 'Show'} Embedded View
              </Button>
            </Box>
          </Grid>
        </Grid>

        {selectedPatientData && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Selected: <strong>{selectedPatientData.name}</strong> (MRN: {selectedPatientData.mrn})
          </Alert>
        )}
      </Paper>

      {/* Embedded View */}
      {embeddedMode && selectedPatient && (
        <Paper sx={{ p: 2, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Embedded Lab Reports Component
          </Typography>
          <LabReports 
            patientId={selectedPatient}
            patient={selectedPatientData}
            showPatientHeader={true}
          />
        </Paper>
      )}

      {/* Usage Examples */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Usage Examples
        </Typography>
        
        <Typography variant="body2" sx={{ mb: 2 }}>
          The Lab Reports component can be used in various ways:
        </Typography>

        <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.875rem' }}>
          <Typography variant="body2" gutterBottom>
            {`// Basic usage in patient encounter`}
          </Typography>
          <Typography variant="body2" gutterBottom>
            {`<LabReports patientId="patient-123" patient={patientData} />`}
          </Typography>
          <br />
          <Typography variant="body2" gutterBottom>
            {`// Usage in modal/dialog`}
          </Typography>
          <Typography variant="body2" gutterBottom>
            {`<Dialog open={open} maxWidth="xl" fullWidth>`}
          </Typography>
          <Typography variant="body2" gutterBottom>
            {`  <DialogContent>`}
          </Typography>
          <Typography variant="body2" gutterBottom>
            {`    <LabReports patientId={patientId} patient={patient} showPatientHeader={false} />`}
          </Typography>
          <Typography variant="body2" gutterBottom>
            {`  </DialogContent>`}
          </Typography>
          <Typography variant="body2">
            {`</Dialog>`}
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Demo Mode:</strong> This component automatically falls back to mock data when Firebase permissions 
            are not available. It will first try to fetch real data from your Firestore collections 
            (`procedure_report`, `procedure_result`, `testDefinitions`), and if that fails due to permissions 
            or missing data, it will display realistic sample lab results for demonstration purposes.
          </Typography>
        </Alert>
      </Paper>

      {/* Dialog Demo */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
        <DialogTitle>
          Lab Reports - {selectedPatientData?.name}
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {selectedPatient && (
            <LabReports 
              patientId={selectedPatient}
              patient={selectedPatientData}
              showPatientHeader={false}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default LabReportsDemo; 